import React, { useEffect, useRef, useState } from 'react';
import { 
  FaClock, 
  FaShieldAlt, 
  FaTools, 
  FaStar,
  FaArrowRight,
  FaPhoneAlt,
  FaMedal,
  FaUsers,
  FaThumbsUp,
  FaTrophy,
  FaCheckCircle,
  FaWrench,
  FaCar,
  FaMotorcycle,
  FaHeadset,
  FaCalendarCheck,
  FaMoneyBillWave,
  FaCertificate
} from 'react-icons/fa';
import { GiCarKey, GiMechanicGarage, GiGearStick, GiOilPump } from 'react-icons/gi';
import { MdOutlineSpeed, MdOutlineCarRepair, MdOutlineLocalCarWash } from 'react-icons/md';

const WhyChooseUs = () => {
  const sectionRef = useRef(null);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [flippedCard, setFlippedCard] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const mainFeatures = [
    {
      id: 1,
      title: 'Expert Technicians',
      description: 'Certified mechanics with 10+ years experience in handling all vehicle types',
      icon: <FaTools />,
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-gradient-to-br from-red-50 to-red-100',
      iconColor: 'text-red-600',
      stats: '15+ Experts',
      details: ['ASE Certified', 'Background Verified', 'Continuous Training']
    },
    {
      id: 2,
      title: 'Genuine Parts',
      description: '100% authentic spare parts with manufacturer warranty on all replacements',
      icon: <FaShieldAlt />,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
      iconColor: 'text-blue-600',
      stats: '2 Year Warranty',
      details: ['OEM Certified', 'Quality Tested', 'Brand New Parts']
    },
    {
      id: 3,
      title: 'Quick Service',
      description: 'Express service delivery with same day turnaround for most repairs',
      icon: <FaClock />,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-gradient-to-br from-green-50 to-green-100',
      iconColor: 'text-green-600',
      stats: '24-48 Hours',
      details: ['Express Delivery', 'Real-time Tracking', 'Priority Service']
    },
    {
      id: 4,
      title: 'Best Price Guarantee',
      description: 'Transparent pricing with no hidden costs and price match guarantee',
      icon: <FaMedal />,
      color: 'from-yellow-500 to-yellow-600',
      bgColor: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
      iconColor: 'text-yellow-600',
      stats: 'Save 30%',
      details: ['No Hidden Fees', 'Price Match', 'EMI Options']
    }
  ];

  const additionalFeatures = [
    {
      id: 5,
      title: 'Free Pickup & Drop',
      description: 'Complimentary vehicle pickup and delivery within 5km radius',
      icon: <GiCarKey />,
      color: 'from-purple-500 to-purple-600',
      iconColor: 'text-purple-600'
    },
    {
      id: 6,
      title: '24/7 Support',
      description: 'Round-the-clock customer support for emergencies',
      icon: <FaHeadset />,
      color: 'from-indigo-500 to-indigo-600',
      iconColor: 'text-indigo-600'
    },
    {
      id: 7,
      title: 'Happy Customers',
      description: '98% customer satisfaction rate with 5000+ happy clients',
      icon: <FaUsers />,
      color: 'from-pink-500 to-pink-600',
      iconColor: 'text-pink-600'
    },
    {
      id: 8,
      title: 'Certified Service',
      description: 'ISO certified service center with quality assurance',
      icon: <FaCertificate />,
      color: 'from-teal-500 to-teal-600',
      iconColor: 'text-teal-600'
    }
  ];

  const images = [
    {
      id: 1,
      src: 'https://t4.ftcdn.net/jpg/02/88/22/59/360_F_288225933_DSRLmL3wzKxGTIQp2NruX2rw8SrgH5dL.jpg',
      alt: 'Car Service Workshop',
    },
    {
      id: 2,
      src: 'https://img.freepik.com/premium-photo/car-detailing-concept-man-face-mask-with-orbital-polisher-repair-shop-polishing-orange-suv-car_627829-12613.jpg?w=2000',
      alt: 'Expert Mechanic at Work',
    },
    {
      id: 3,
      src: 'https://media.istockphoto.com/id/495925010/photo/father-and-daughter-working-in-automobile-repair-shop.jpg?s=612x612&w=0&k=20&c=UNCVMxA8sLtFVTnMOxoCZefBLBHK1rORos5nhN40ueI=',
      alt: 'Bike Service',
    },
    {
      id: 4,
      src: 'https://tse4.mm.bing.net/th/id/OIP.X0q1igogzIpXL9pJ_azfFAHaE8?rs=1&pid=ImgDetMain&o=7&rm=3',
      alt: 'Car AC Service',
    }
  ];

  const statsData = [
    { icon: <FaUsers />, value: '5000+', label: 'Happy Customers', color: 'text-red-600' },
    { icon: <FaTrophy />, value: '10+', label: 'Years Experience', color: 'text-yellow-600' },
    { icon: <FaClock />, value: '24/7', label: 'Support Available', color: 'text-green-600' },
    { icon: <FaThumbsUp />, value: '98%', label: 'Satisfaction', color: 'text-blue-600' }
  ];

  return (
    <section ref={sectionRef} className="py-8 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse animation-delay-2000"></div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Header Section */}
        <div className="text-center mb-12 animate-on-scroll opacity-0">
          <h2 className="text-3xl md:text-3xl font-bold text-gray-900 mb-4">
            Why <span className="relative inline-block">
              <span className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-500 blur-2xl opacity-30"></span>
              <span className="relative bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
                Ambris Service
              </span>
            </span> Center?
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-lg">
            Delivering excellence in vehicle care with transparency, quality, and customer satisfaction
          </p>
        </div>

        {/* Main Features Cards - Grid with Detailed Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {mainFeatures.map((feature, index) => (
            <div
              key={feature.id}
              className="group animate-on-scroll opacity-0"
              style={{ animationDelay: `${index * 0.1}s` }}
              onMouseEnter={() => setFlippedCard(feature.id)}
              onMouseLeave={() => setFlippedCard(null)}
            >
              <div className="relative h-full">
                {/* Front Card */}
                <div className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden ${flippedCard === feature.id ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                  <div className={`absolute top-0 right-0 w-24 h-24 ${feature.bgColor} rounded-bl-full opacity-50`}></div>
                  <div className="p-6">
                    <div className={`w-14 h-14 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-4 shadow-lg transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}>
                      <div className="text-white text-2xl">{feature.icon}</div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                      {feature.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                      {feature.description}
                    </p>
                    
                    <div className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-lg">
                      <FaStar className="text-xs" />
                      <span>{feature.stats}</span>
                    </div>
                    
                    {/* Hover Indicator */}
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                        <FaArrowRight className="text-white text-xs" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back Card with Details */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.bgColor} rounded-2xl shadow-xl p-6 transition-all duration-500 ${
                  flippedCard === feature.id ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                }`}>
                  <div className="h-full flex flex-col">
                    <div className="mb-4">
                      <div className="text-3xl mb-3">{feature.icon}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <div className="h-1 w-12 bg-red-600 rounded"></div>
                    </div>
                    
                    <div className="flex-1">
                      <p className="text-gray-700 text-sm mb-4 font-semibold">Key Highlights:</p>
                      <ul className="space-y-2">
                        {feature.details.map((detail, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                            <FaCheckCircle className="text-green-500 text-xs" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <button className="mt-4 bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors">
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Gallery - 4 Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12 animate-on-scroll opacity-0">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
              onMouseEnter={() => setHoveredImage(image.id)}
              onMouseLeave={() => setHoveredImage(null)}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-5 transform transition-transform duration-500">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-white font-bold text-lg">{image.title}</h3>
                  </div>
                  <p className="text-white/80 text-sm">{image.subtitle}</p>
                  
                  {/* Hover Button */}
                  <div className={`mt-3 overflow-hidden transition-all duration-300 ${
                    hoveredImage === image.id ? 'max-h-12 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-up {
          animation: fadeUp 0.6s ease-out forwards;
        }
        
        .animate-on-scroll {
          opacity: 0;
        }
        
        .animate-on-scroll.animate-fade-up {
          opacity: 1;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </section>
  );
};

export default WhyChooseUs;